import * as React from 'react';
import { Droppable } from 'react-beautiful-dnd';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import Card from 'af-root/scenes/Company/ScheduleBoard/Shared/Card';
import BlankCard from 'af-root/scenes/Company/ScheduleBoard/Shared/Card/BlankCard';

import type { RootState } from 'af-reducers';

import * as ScheduleBoardUtil from 'af-utils/scheduleBoard.util';

import * as ScheduleBoardActions from 'af-actions/scheduleBoard';

const DEFAULT_WORK_ORDER_ROW_DISTRIBUTION = [];

interface OwnProps {
	addBlankWorkOrder: (dueDate: string, index: number) => Promise<void>;
	className: string;
	droppableId: string;
	/** `MM-DD-YYYY` */
	dueDate: string;
	forceUnlockOrder: (workOrderId: string) => void;
	hasPermissionsToEditScheduleBoard: boolean;
	isDragAndDropDisabled: boolean;
	lastOpenedOrderCode: string;
	removeBlankWorkOrder: (dueDate: string, index: number) => Promise<void>;
	rowIndex: number;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const DroppableComponent: React.FC<Props> = (props) => {
	const {
		addBlankWorkOrder,
		deselectOrder,
		dueDate,
		forceUnlockOrder,
		hasPermissionsToEditScheduleBoard,
		isDragAndDropDisabled,
		lastOpenedOrderCode,
		removeBlankWorkOrder,
		selectOrder,
		rowIndex,
		droppableId,
		isDisabled,
		className,
		workOrdersRowDistribution,
	} = props;

	const startIndex = React.useMemo(() => ScheduleBoardUtil.getStartIndexForRow(workOrdersRowDistribution, rowIndex), [rowIndex, workOrdersRowDistribution]);

	const renderCards = React.useCallback(() => {

		return (
			<div className="schedule-board-cards-container">
				{(workOrdersRowDistribution?.[rowIndex] ?? []).map((workOrderCode: string, index: number) => {
					const isBlank = ScheduleBoardUtil.isBlankWorkOrderId(workOrderCode);
					const isPlaceholder = ScheduleBoardUtil.isLoadingPlaceholderDroppableId(workOrderCode);
					return isBlank || isPlaceholder ?
						(
							<BlankCard
								draggableId={ScheduleBoardUtil.generateBlankWorkOrderId(dueDate, startIndex + index)}
								dueDate={dueDate}
								hasPermissionsToEditScheduleBoard={hasPermissionsToEditScheduleBoard}
								index={startIndex + index}
								isDragAndDropDisabled={isDragAndDropDisabled}
								isPlaceholder={isPlaceholder}
								key={index}
								removeBlankWorkOrder={removeBlankWorkOrder}
							/>
						) : (
							<Card
								addBlankWorkOrder={addBlankWorkOrder}
								deselectOrder={deselectOrder}
								dueDate={dueDate}
								forceUnlockOrder={forceUnlockOrder}
								hasPermissionsToEditScheduleBoard={hasPermissionsToEditScheduleBoard}
								index={startIndex + index}
								isDragAndDropDisabled={isDragAndDropDisabled}
								key={index}
								lastOpenedOrderCode={lastOpenedOrderCode}
								selectOrder={selectOrder}
								workOrderCode={workOrderCode}
							/>
						);
				})}
			</div>
		);
	}, [
		addBlankWorkOrder,
		deselectOrder,
		dueDate,
		forceUnlockOrder,
		hasPermissionsToEditScheduleBoard,
		isDragAndDropDisabled,
		lastOpenedOrderCode,
		removeBlankWorkOrder,
		rowIndex,
		selectOrder,
		startIndex,
		workOrdersRowDistribution,
	]);

	const renderDraggable = React.useCallback((provided, snapshot) => {
		return (
			<div
				{...(provided.droppableProps)}
				className={`${className} ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
				ref={provided.innerRef}
			>
				{renderCards()}
				{provided.placeholder}
			</div>
		);
	}, [className, renderCards]);

	return (
		<Droppable
			direction="horizontal"
			droppableId={droppableId}
			isDropDisabled={isDisabled}
		>
			{renderDraggable}
		</Droppable>
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { dueDate, isDragAndDropDisabled, hasPermissionsToEditScheduleBoard } = ownProps;
	return {
		isDisabled: !state.scheduleBoard.draggedWorkOrderCode || isDragAndDropDisabled || !hasPermissionsToEditScheduleBoard,
		workOrdersRowDistribution: state?.scheduleBoard?.workOrdersByDateDictionary?.[dueDate]?.workOrdersRowDistribution
			?? DEFAULT_WORK_ORDER_ROW_DISTRIBUTION,
	};
}

function mapDispatchToProps() {
	return {
		selectOrder: ScheduleBoardActions.selectWorkOrder,
		deselectOrder: ScheduleBoardActions.deselectWorkOrder,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(DroppableComponent);
